import { supabase } from '../supabase';

/* ============================================
   Constants
   ============================================ */

export const CATEGORY_OPTIONS = [
  { value: 'technical',     label: 'Technical' },
  { value: 'leadership',    label: 'Leadership' },
  { value: 'communication', label: 'Communication' },
  { value: 'domain',        label: 'Domain' },
  { value: 'other',         label: 'Other' },
];

export const VALID_CATEGORIES = CATEGORY_OPTIONS.map(c => c.value);

export const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, c) => {
  acc[c.value] = c.label;
  return acc;
}, {});

// 5 distinct line colors per spec (electric blue, gold, purple, green, pink)
export const LINE_COLORS = ['#1e90ff', '#c9a227', '#9d6eff', '#2ea043', '#ff6b9d'];

/* ============================================
   Helpers
   ============================================ */

export function levelLabel(score) {
  if (score >= 80) return 'Expert';
  if (score >= 60) return 'Advanced';
  if (score >= 40) return 'Intermediate';
  return 'Beginner';
}

export function coerceCategory(cat) {
  if (!cat) return 'other';
  const slug = String(cat).toLowerCase().trim();
  return VALID_CATEGORIES.includes(slug) ? slug : 'other';
}

/* ============================================
   Supabase queries
   ============================================ */

export async function loadSkills(userId) {
  const { data, error } = await supabase
    .from('skill_scores')
    .select('*')
    .eq('user_id', userId)
    .order('recorded_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function insertSkillScore({ user_id, skill_name, category, score, notes, source }) {
  const payload = {
    user_id,
    skill_name: String(skill_name).trim(),
    category: coerceCategory(category),
    score: Math.max(0, Math.min(100, Math.round(Number(score) || 0))),
    source: source || 'self-rated',
  };
  if (notes && String(notes).trim()) payload.notes = String(notes).trim();
  const { error } = await supabase.from('skill_scores').insert(payload);
  if (error) throw error;
}

// Delete ALL rows for a given (user_id, skill_name) pair. True delete — wipes history.
//
// We chain .select() so Supabase returns the deleted rows. If the array is
// empty, no rows were actually affected — almost always an RLS policy missing
// for DELETE on this table. Without this check, RLS-blocked deletes silently
// succeed, the UI gets a misleading "deleted" toast, and the row stays.
export async function deleteSkillByName({ user_id, skill_name }) {
  if (!user_id || !skill_name) throw new Error('Missing user_id or skill_name');
  const { data, error } = await supabase
    .from('skill_scores')
    .delete()
    .eq('user_id', user_id)
    .eq('skill_name', skill_name)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Delete didn't affect any rows. Check your Supabase RLS policy for DELETE on skill_scores.");
  }
  return data.length; // number of rows deleted
}

// Rename a skill across ALL historical rows. Returns the count of rows updated.
// NOTE: Caller is responsible for inserting a fresh "now" row after this if a
// new score/category/notes also need to be recorded as a new event.
//
// Same RLS-verification pattern as deleteSkillByName: a missing UPDATE policy
// silently no-ops on RLS tables. Chain .select() and throw on empty.
export async function renameSkillName({ user_id, old_name, new_name }) {
  if (!user_id || !old_name || !new_name) throw new Error('Missing user_id, old_name, or new_name');
  const trimmedNew = String(new_name).trim();
  if (!trimmedNew) throw new Error('New skill name cannot be empty');
  const { data, error } = await supabase
    .from('skill_scores')
    .update({ skill_name: trimmedNew })
    .eq('user_id', user_id)
    .eq('skill_name', old_name)
    .select();
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("Rename didn't affect any rows. Check your Supabase RLS policy for UPDATE on skill_scores.");
  }
  return data.length;
}

/* ============================================
   Transformations
   ============================================ */

// Group rows by skill_name, return the most recent row per skill.
// Assumes rows are pre-sorted by recorded_at DESC (which loadSkills does).
export function getCurrentSkills(rows) {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.skill_name)) {
      map.set(r.skill_name, r);
    }
  }
  return Array.from(map.values());
}

// Avg score per category, including zero-fill for empty categories.
// Returns array of { category: <Label>, score: <int> } in CATEGORY_OPTIONS order.
export function getRadarData(currentSkills) {
  const totals = {};
  const counts = {};
  for (const c of CATEGORY_OPTIONS) {
    totals[c.value] = 0;
    counts[c.value] = 0;
  }
  for (const s of currentSkills) {
    const cat = coerceCategory(s.category);
    totals[cat] += s.score;
    counts[cat] += 1;
  }
  return CATEGORY_OPTIONS.map(c => ({
    category: c.label,
    score: counts[c.value] > 0 ? Math.round(totals[c.value] / counts[c.value]) : 0,
  }));
}

// Build last 6 months of data for the top-N skills by current score, with
// carry-forward and a `<skill>_real` flag per point so dots only render at
// actual recorded entries.
//
// Returns:
//   { data: [{ month: 'May 2026', Python: 70, Python_real: true, ... }],
//     skills: ['Python', ...] }
export function getTrajectoryData(rows, currentSkills) {
  if (currentSkills.length === 0) return { data: [], skills: [] };

  const top = [...currentSkills]
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map(s => s.skill_name);

  // Last 6 month buckets ending in current month
  const now = new Date();
  const buckets = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    buckets.push({ key, label });
  }
  const bucketKeys = new Set(buckets.map(b => b.key));

  // For each top skill, find the latest entry in each month (rows are DESC sorted)
  const skillMonthLatest = {};
  for (const skill of top) {
    skillMonthLatest[skill] = {};
  }
  for (const r of rows) {
    if (!skillMonthLatest[r.skill_name]) continue;
    const d = new Date(r.recorded_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!bucketKeys.has(key)) continue;
    if (!skillMonthLatest[r.skill_name][key]) {
      skillMonthLatest[r.skill_name][key] = r; // first encountered = latest
    }
  }

  // Carry-forward fill across buckets
  const lastSeen = {};
  for (const skill of top) lastSeen[skill] = null;

  const data = buckets.map(b => {
    const row = { month: b.label };
    for (const skill of top) {
      const entry = skillMonthLatest[skill][b.key];
      if (entry) {
        row[skill] = entry.score;
        row[`${skill}_real`] = true;
        lastSeen[skill] = entry.score;
      } else if (lastSeen[skill] !== null) {
        row[skill] = lastSeen[skill];
        row[`${skill}_real`] = false;
      } else {
        row[skill] = null; // pre-first-observation gap
        row[`${skill}_real`] = false;
      }
    }
    return row;
  });

  return { data, skills: top };
}
